import * as R from 'ramda';
import { debounce } from 'throttle-debounce';

import { b64DecodeUnicode, b64EncodeUnicode } from '@utils/funcs.ts';

const ONE_SECOND = 1000; // 1000ms = 1 second
const STARTUP_WAIT_MS = 15 * ONE_SECOND; // longest we hold URL writes waiting on system/readywhenyouare

/*
  Track latest state update here. Format is pluginId -> pluginState, e.g.:
    latestState = {
        "mapRenderer": { <mapRendererState> }
        "mapLevelSelector": { <mapLevelSelectorState> }
      }
*/
let latestState = {};

/**
 * Given a state object (in the form of latestState above), update the URL to reflect it.
 * @param  {Object} state app state in form of latestState above
 * @parm   {any} allows preservation of lldebug URL parameter (which becomes app.config.debug)
 */
function updateURLActual(state, lldebug, lastPoppedStateHash, lastPushedStateHash, preservedQueryParams = {}) {
  const encodedState = base64Encode(state);
  const stateHash = simpleHash(encodedState);
  if (stateHash !== lastPoppedStateHash && stateHash !== lastPushedStateHash) {
    const url = location.origin + location.pathname + '?' + getStateParms(state, lldebug, preservedQueryParams);
    // We can not use pushState here because managing browser back/foward history is much harder than just updating the url.
    history.replaceState(null, document.title, url);
  }
}

const updateURL = debounce(ONE_SECOND, updateURLActual); // wait until the user rests for 1 second before storing URL in history

// "State Parms" are parameters that are persisted explicitly in the URL
function getStateParms(state, lldebug, preservedQueryParams: Record<string, string> = {}) {
  const queryParms = {
    ...preservedQueryParams,
    ...R.pipe(R.propOr({}, 'venueDataLoader'), R.pick(['vid', 'stage', 'lang']))(state),
  };

  if (lldebug !== undefined) {
    queryParms.lldebug = lldebug;
  }

  queryParms.s = base64Encode(clean(state)); // plugin state as array of objects, Base64-encoded

  return Object.entries(queryParms)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

// strip an object of all its nullish (null or undefined) values
const removeNullishValues = ob => {
  const ret = {};
  for (const key of Object.keys(ob)) {
    if (ob[key] != null) {
      ret[key] = ob[key];
    }
  }
  return ret;
};

const clean = state => {
  // This section removes the 'id' property from each value (as its in the key), and
  // removes vid, state, lang from venueDataLoader values
  const cleanState = R.map(o => {
    const { id, ...o2 } = o;
    if (id === 'venueDataLoader') {
      delete o2.vid;
      delete o2.lang;
      delete o2.stage;
    }
    return o2;
  }, state);

  // this section removes any properties that are null or undefined
  for (const pname of Object.keys(cleanState)) {
    cleanState[pname] = removeNullishValues(cleanState[pname]);
  }

  // And finally we remove any empty plugin property objects
  const nonEmptyState = R.filter(o => !R.isEmpty(o), cleanState);

  return nonEmptyState;
};

// consider normalizing JSON using something like this: https://github.com/substack/json-stable-stringify
const base64Encode = state => b64EncodeUnicode(JSON.stringify(state));

/**
 * Given a state string (JSON representation of latestState above) this will
 * update the app state (via calling all plugins with their state)
 * @param  {Object} app App object created in App.js
 * @param  {string} stateString stringified JSON object representing state
 */
function setStateFromStateString(app, stateString) {
  latestState = JSON.parse(stateString);
  // Iterate through each state object, which is a plugin id and an
  Object.keys(latestState).forEach(id => {
    const so = { id, ...latestState[id] };
    // call plugin if it was recently visible
    // if (latestState[LAYER_MANAGER_ID].includes(so.id)) // Glenn: hmmm, I think we need to set state in non-visible plugins as well..
    app.bus.send('deepLinking/setState', so);
  });
}

/**
 * Build initial state from the URL the app was loaded with. Plugins report their state
 * one at a time during startup, so without this the first URL write would drop any
 * parameter (vid, lang, s) whose owning plugin hasn't reported yet.
 * @param  {string} search location.search at startup
 * @return {Object} state in the form of latestState above
 */
function stateFromURL(search) {
  const parms = new URLSearchParams(search);
  const state = {};

  const stateString64 = parms.get('s');
  if (stateString64) {
    try {
      const urlState = JSON.parse(b64DecodeUnicode(stateString64));
      for (const [id, pluginState] of Object.entries(urlState)) {
        state[id] = { id, ...pluginState };
      }
    } catch {
      // malformed s parameter - nothing to preserve
    }
  }

  const venueParms = R.pick(['vid', 'stage', 'lang'], Object.fromEntries(parms));
  if (!R.isEmpty(venueParms)) {
    state.venueDataLoader = { ...state.venueDataLoader, id: 'venueDataLoader', ...venueParms };
  }

  return state;
}

// This is a simple, *insecure* hash that's short, fast, and cryptographically insecure.
// See https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
const simpleHash = str => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }
  return new Uint32Array([hash])[0].toString(36);
};

function create(app, config) {
  const preservedQueryParams = config.preservedQueryParams ?? {};

  let lastPoppedStateHash = null;
  const lastPushedStateHash = null;

  // URL writes are held until startup settles (see init), then flushed once if any state arrived meanwhile
  let urlWritesEnabled = false;
  let urlWritePending = false;
  const writeURL = () =>
    updateURL(
      latestState,
      JSON.stringify(app.config.debug),
      lastPoppedStateHash,
      lastPushedStateHash,
      preservedQueryParams,
    );

  if (config.trackURL && typeof window !== 'undefined') {
    latestState = stateFromURL(window.location.search);
  }

  // Every plugin has been created (and registered its listeners) before any init runs, so
  // system/readywhenyouare here waits on all participants - including venueDataLoader, which
  // holds it until it has reported vid/lang. A failing or hung participant (e.g. a dynamicPois fetch
  // with no timeout) must not freeze the URL, so the wait is bounded. If it times out first, the state
  // seeded from the startup URL above still keeps vid/lang/s in the first write.
  const init = async () => {
    await app.bus
      .send('system/readywhenyouare')
      .timeout(STARTUP_WAIT_MS)
      .catch(() => {});
    urlWritesEnabled = true;
    if (urlWritePending) {
      writeURL();
    }
  };

  app.bus.on('deepLinking/getLatestState', () => latestState);

  app.bus.on('deepLinking/getEncodedState', () => base64Encode(clean(latestState)));
  app.bus.on('deepLinking/getStateParms', () => getStateParms(latestState, undefined, preservedQueryParams));

  // app.bus.on('app/reset', () => {
  //   app.bus.send('deepLinking/notifyState', { id: 'directionsSearchView', widgetState: undefined })
  //   app.bus.send('deepLinking/notifyState', { id: 'getDirectionsFromTo', widgetState: undefined })
  //   app.bus.send('deepLinking/notifyState', { id: 'mapLevelSelectorOnline', selectedBuildingId: null, selectedLevel: null })
  //   app.bus.send('deepLinking/notifyState', { id: 'poiView', poiId: null })
  // })

  app.bus.on('deepLinking/setEncodedState', ({ state }) => setStateFromStateString(app, b64DecodeUnicode(state)));

  app.bus.on('deepLinking/notifyState', async state => {
    if (!state.id) {
      throw Error("State notified in deepLinking/notifyState with no 'id' field present: " + JSON.stringify(state));
    }
    latestState[state.id] = state;

    // save info about currently visible layers
    // latestState[LAYER_MANAGER_ID] = (await app.bus.get('layers/getVisibleLayersIds'))
    const gdft = latestState['online/getDirectionsFromTo'];
    if (gdft && gdft.navTo) {
      delete latestState['online/poiView'];
    }

    if (config.trackURL) {
      if (urlWritesEnabled) {
        writeURL();
      } else {
        urlWritePending = true;
      }
    }
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
      const searchParams = new URLSearchParams(window.location.search);
      const stateParam = searchParams.get('s');
      if (stateParam) {
        const state = b64DecodeUnicode(stateParam);
        lastPoppedStateHash = simpleHash(stateParam);
        setStateFromStateString(app, state);
      }
    });
  }

  return {
    init,
  };
}

export { create };
